import { useLocation, useNavigate } from "react-router-dom";
import BreadCumb from "../../components/common/Breadcrumb";
import backArrow from "../../assets/task/back_arrow.svg";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import InputsNew from "../../components/common/InputsNew";
import SearchableInputsDropdown from "../../components/common/SearchableDropdown";
import { useEffect, useState } from "react";
import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { EMPLOYEES_BY_AGENCY_ID, TODOS } from "../../api/query";
import Loader from "../../components/common/Loader";
import { useAuth } from "../../context/AuthContext";
import { CREATE_UPDATE_TODO } from "../../api/mutation";
import {
  showError,
  showSuccess,
} from "../../components/common/ToastNotification";
import DateSelector from "../../components/common/DateSelector";

interface EditTodo {
  title: string;
  description?: string;
  dueDate: string;
  assignedToID: string;
  assignedToName: string;
  meetingId?: number;
}

// Validation schema
const editTodoSchema = yup.object().shape({
  title: yup
    .string()
    .test(
      "not-only-whitespace",
      "Please add a valid Title",
      (value) => !value || value.trim().length > 0,
    )
    .required("This field is required"),
  description: yup.string().nullable(),
  dueDate: yup.string().required("This field is required"),
  assignedToID: yup.string().required("This field is required"),
  assignedToName: yup.string().required("This field is required"),
  meetingId: yup.number().nullable(),
});

const EditTodo = () => {
  const location = useLocation();
  const state = location.state as {
    id: any;
    viewOnly: any;
  };

  const { id, viewOnly } = state;

  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const employeeID = userInfo?.employee_id || "";
  const organizationID = userInfo?.agencies?.id || "";

  const [assignedMemberOptions, setAssignedMemberOptions] = useState<
    { key: string; name: string }[]
  >([]);
  const [isEditMode, setIsEditMode] = useState(false);

  //api
  const [getToDos, { loading, data }] = useLazyQuery(TODOS, {
    fetchPolicy: "no-cache",
  });

  const [
    getEmployeesByAgencyID,
    { data: employeesData, loading: employeesDataLoading },
  ] = useLazyQuery(EMPLOYEES_BY_AGENCY_ID, {
    fetchPolicy: "no-cache",
  });

  const [createUpdateToDo, { loading: createUpdateToDoLoading }] = useMutation(
    CREATE_UPDATE_TODO,
    {
      fetchPolicy: "no-cache",
    },
  );

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    trigger,
    watch,
    control,
  } = useForm<any>({
    resolver: yupResolver(editTodoSchema),
  });

  useEffect(() => {
    if (id) {
      getToDos({
        variables: {
          id: parseInt(id),
        },
      });
    }
  }, [id]);

  useEffect(() => {
    if (data?.todos?.status_code === 200) {
      const todo = data?.todos?.todos[0];

      setValue("title", todo?.title);
      setValue("dueDate", todo?.due_date?.split("T")[0]);
      setValue("assignedToName", todo?.todo_assigned_user_name);
      setValue("assignedToID", todo?.todo_assigned_user?.employee_id);
      if (todo?.description) setValue("description", todo.description);
      if (todo?.meeting_id) setValue("meetingId", todo.meeting_id);
    }
  }, [data]);

  useEffect(() => {
    if (organizationID) {
      getEmployeesByAgencyID({
        variables: {
          agency_id: parseInt(organizationID),
        },
      });
    }
  }, [organizationID]);

  useEffect(() => {
    if (employeesData && employeesData.employees_by_agency_id) {
      const employeeOptions =
        employeesData?.employees_by_agency_id?.employees?.map((emp: any) => ({
          key: emp.employee_id,
          name: `${emp.first_name} ${emp.last_name}`,
        }));
      setAssignedMemberOptions(employeeOptions);
    }
  }, [employeesData]);

  //watch
  const watchAssignedMember: string = watch("assignedToName");

  function handleSelectAssignee(key: string, name: string): void {
    setValue("assignedToID", key);
    setValue("assignedToName", name);
    trigger("assignedToName");
  }

  async function onSubmit(data: EditTodo) {
    const input: any = {
      id: parseInt(id),
      title: data.title,
      todo_assigned_user: parseInt(data.assignedToID),
      due_date: `${data.dueDate}T00:00:00`,
    };
    if (data.description?.trim()) input.description = data.description.trim();
    if (data.meetingId) input.meeting_id = parseInt(String(data.meetingId));

    try {
      const response = await createUpdateToDo({
        variables: {
          created_by: parseInt(employeeID),
          input: input,
        },
      });
      if (response.data.create_update_todo.status_code === 200) {
        showSuccess(response.data.create_update_todo.message);
        navigate(-1);
      }
      if (response.data.create_update_todo.status_code === 400) {
        showError(response.data.create_update_todo.message);
      }
    } catch (error) {
      if (error instanceof ApolloError) {
        showError(error.message);
      }
    }
  }

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        id="cts-respon-p-dv"
        className="w-full  relative  overflow-hidden flex flex-row items-start justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4  box-border text-left text-base text-gray font-manrope"
      >
        <div className="flex-1 flex flex-col items-start justify-start gap-6">
          <BreadCumb />
          <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
            <img
              className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
              alt=""
              src={backArrow}
              onClick={() => navigate(-1)}
            />

            <div className="flex justify-between w-full">
              <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">
                {isEditMode ? "Edit" : "View"} To Do
              </h3>
        {
          !loading &&      <div className="border-[1px] border-solid border-surface-border cursor-pointer rounded-radius-sm box-border dark:bg-slate-700 grid flex-col items-start justify-center p-1.5 gap-[8px] text-left text-sm text-dark-font font-body-reg border-[1px] border-solid border-surface-border dark:bg-slate-700 mq575:min-w-full">
                {data?.todos.todos[0].is_completed
                  ? "Completed"
                  : "Not Completed"}
              </div>
        }
         
            </div>
          </div>
          <div className="m-0 self-stretch rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove flex flex-col items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
              <InputsNew
                contentPlaceholder="Title"
                label="Title*"
                type="text"
                register={register("title")}
                errorMessage={errors.title}
                disabled={!isEditMode}
              />
            </div>
            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[8px] min-w-full">
              <Controller
                name="dueDate"
                control={control}
                render={({ field }) => (
                  <DateSelector
                    label="Due Date*"
                    selectedDate={field.value}
                    onChange={(date: any) => {
                      field.onChange(date);
                      setValue("dueDate", date);
                      trigger("dueDate");
                    }}
                    error={errors.dueDate?.message}
                    nothideParsedValue={true}
                    minDate={new Date()}
                    disabled={!isEditMode}
                  />
                )}
              />
            </div>
            <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[8px] min-w-full">
              <SearchableInputsDropdown
                label="Assign To*"
                initialValue={watchAssignedMember}
                placeHolder="Select Member"
                options={assignedMemberOptions}
                onSelect={handleSelectAssignee}
                errorMessage={errors?.assignedToName}
                disableDropdown={!isEditMode}
              />
            </div>
            <InputsNew
              contentPlaceholder="Description"
              label="Description"
              type="textarea"
              register={register("description")}
              errorMessage={errors.description}
              disabled={!isEditMode}
            />
            {!viewOnly && (
              <div className="self-stretch flex flex-row items-center justify-end py-0 pr-0 gap-[8px]">
                <button
                  type="button"
                  className="cursor-pointer bg-white py-2.5 h-[44px] px-[42px] btn-text-purple rounded-radiuscomponent-button border-[2px] border-solid border-surface-primary-surface"
                  onClick={() => {
                    if (isEditMode) {
                      setIsEditMode(false);
                    } else {
                      navigate(-1);
                    }
                  }}
                >
                  <div className="relative text-sm font-body-xs-semi-bold">
                    Cancel
                  </div>
                </button>

                {!isEditMode ? (
                  <button
                    type="button"
                    className="cursor-pointer py-2.5 h-[44px] px-[42px] bg-surface-primary-surface rounded-radiuscomponent-button"
                    onClick={(e: any) => {
                      e.preventDefault();
                      setIsEditMode(true);
                    }}
                  >
                    <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary">
                      Edit
                    </div>
                  </button>
                ) : (
                  <button
                    type="submit"
                    className="cursor-pointer py-2.5 h-[44px] px-[42px] bg-surface-primary-surface rounded-radiuscomponent-button"
                  >
                    <div className="relative text-sm font-body-xs-semi-bold text-surface-bg-primary">
                      Save
                    </div>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </form>
      {(employeesDataLoading || createUpdateToDoLoading || loading) && (
        <Loader />
      )}
    </>
  );
};

export default EditTodo;
